import type { ReactiveController, ReactiveControllerHost, TemplateResult } from 'lit';
import { html } from 'lit';
import { classMap } from 'lit/directives/class-map.js';
import { dispatch } from '../../utils/event-helpers';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface DatePickerPreset {
  key: string;
  label: string;
  active: boolean;
}

export interface DatePickerHost extends ReactiveControllerHost, HTMLElement {
  type: 'single' | 'dual';
  _confirmedDate: string | null;
  _confirmedStartDate: string | null;
  _confirmedEndDate: string | null;
  _startInputError: boolean;
  _endInputError: boolean;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const WEEKDAYS = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'] as const;
const WEEKDAY_FULL = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

const PRESETS: Omit<DatePickerPreset, 'active'>[] = [
  { key: 'today', label: 'Today' },
  { key: 'yesterday', label: 'Yesterday' },
  { key: 'this-week', label: 'This week' },
  { key: 'last-week', label: 'Last week' },
  { key: 'this-month', label: 'This month' },
  { key: 'last-month', label: 'Last month' },
  { key: 'this-year', label: 'This year' },
  { key: 'last-year', label: 'Last year' },
  { key: 'all-time', label: 'All time' },
];

/** Returns YYYY-MM-DD string for a Date. */
function toKey(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** Returns a display string "Jan 10, 2025". */
function toDisplay(d: Date): string {
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

/** Parses "Jan 10, 2025" → Date | null. */
function parseDisplay(s: string): Date | null {
  const parsed = new Date(s);
  return isNaN(parsed.getTime()) ? null : parsed;
}

/** Monday-based start of week for a given date. */
function startOfWeek(d: Date): Date {
  const day = d.getDay(); // 0=Sun
  const diff = (day === 0 ? -6 : 1 - day);
  const r = new Date(d);
  r.setDate(d.getDate() + diff);
  r.setHours(0, 0, 0, 0);
  return r;
}

function endOfWeek(d: Date): Date {
  const start = startOfWeek(d);
  const r = new Date(start);
  r.setDate(start.getDate() + 6);
  return r;
}

function startOfMonth(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}

function endOfMonth(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth() + 1, 0);
}

/** Compute start/end for a preset key. Returns null if unknown. */
function presetRange(key: string): { start: Date; end: Date } | null {
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  switch (key) {
    case 'today': return { start: now, end: now };
    case 'yesterday': {
      const d = new Date(now); d.setDate(now.getDate() - 1);
      return { start: d, end: d };
    }
    case 'this-week': return { start: startOfWeek(now), end: endOfWeek(now) };
    case 'last-week': {
      const s = startOfWeek(now); s.setDate(s.getDate() - 7);
      const e = endOfWeek(s);
      return { start: s, end: e };
    }
    case 'this-month': return { start: startOfMonth(now), end: endOfMonth(now) };
    case 'last-month': {
      const s = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      return { start: s, end: endOfMonth(s) };
    }
    case 'this-year': return {
      start: new Date(now.getFullYear(), 0, 1),
      end: new Date(now.getFullYear(), 11, 31),
    };
    case 'last-year': return {
      start: new Date(now.getFullYear() - 1, 0, 1),
      end: new Date(now.getFullYear() - 1, 11, 31),
    };
    case 'all-time': return { start: new Date(2000, 0, 1), end: now };
    default: return null;
  }
}

// ---------------------------------------------------------------------------
// Controller
// ---------------------------------------------------------------------------

export class DatePickerController implements ReactiveController {
  // ── Pending (uncommitted) state ──────────────────────────────────────────
  pendingDate: Date | null = null;
  pendingStartDate: Date | null = null;
  pendingEndDate: Date | null = null;
  /** For dual: how many clicks into a new range selection (0, 1, or 2) */
  private _clickCount = 0;
  // ── Calendar navigation ──────────────────────────────────────────────────
  displayYear: number;
  displayMonth: number; // 0-indexed
  // ── Input values ────────────────────────────────────────────────────────
  startInputValue = '';
  endInputValue = '';
  // ── Hover state for range preview ───────────────────────────────────────
  private _hoverDate: Date | null = null;

  constructor(private readonly _host: DatePickerHost) {
    const now = new Date();
    this.displayYear = now.getFullYear();
    this.displayMonth = now.getMonth();
    _host.addController(this);
  }

  hostConnected(): void { /* nothing */ }
  hostDisconnected(): void { /* nothing */ }

  // ── Derived labels ───────────────────────────────────────────────────────

  get primaryMonthLabel(): string {
    return new Date(this.displayYear, this.displayMonth, 1)
      .toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  }

  get secondaryMonthLabel(): string {
    const d = new Date(this.displayYear, this.displayMonth + 1, 1);
    return d.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  }

  // ── Presets ──────────────────────────────────────────────────────────────

  get presets(): DatePickerPreset[] {
    return PRESETS.map(p => {
      const range = presetRange(p.key);
      let active = false;
      if (range && this.pendingStartDate && this.pendingEndDate) {
        active =
          toKey(this.pendingStartDate) === toKey(range.start) &&
          toKey(this.pendingEndDate) === toKey(range.end);
      }
      return { ...p, active };
    });
  }

  // ── Navigation ───────────────────────────────────────────────────────────

  navigateMonth(delta: -1 | 1): void {
    const d = new Date(this.displayYear, this.displayMonth + delta, 1);
    this.displayYear = d.getFullYear();
    this.displayMonth = d.getMonth();
    this._host.requestUpdate();
  }

  // ── Single mode ──────────────────────────────────────────────────────────

  selectDay(date: Date): void {
    if (this._host.type === 'single') {
      if (this.pendingDate && toKey(this.pendingDate) === toKey(date)) {
        this.pendingDate = null;
        this.startInputValue = '';
      } else {
        this.pendingDate = date;
        this.startInputValue = toDisplay(date);
        // Navigate to clicked day's month
        this.displayYear = date.getFullYear();
        this.displayMonth = date.getMonth();
      }
      dispatch<{ date: string | null }>(this._host, 'asc-select', {
        detail: { date: this.pendingDate ? toDisplay(this.pendingDate) : null },
      });
    } else {
      // Dual mode state machine
      if (this._clickCount === 0 || this._clickCount === 2) {
        // Start fresh selection
        this.pendingStartDate = date;
        this.pendingEndDate = null;
        this._clickCount = 1;
        this.startInputValue = toDisplay(date);
        this.endInputValue = '';
      } else {
        // Second click — set end, swap if needed
        let start = this.pendingStartDate!;
        let end = date;
        if (end < start) { [start, end] = [end, start]; }
        this.pendingStartDate = start;
        this.pendingEndDate = end;
        this._clickCount = 2;
        this.startInputValue = toDisplay(start);
        this.endInputValue = toDisplay(end);
      }
      dispatch<{ startDate: string | null; endDate: string | null }>(this._host, 'asc-select', {
        detail: {
          startDate: this.pendingStartDate ? toDisplay(this.pendingStartDate) : null,
          endDate: this.pendingEndDate ? toDisplay(this.pendingEndDate) : null,
        },
      });
    }
    this._host.requestUpdate();
  }

  setHover(date: Date | null): void {
    this._hoverDate = date;
    this._host.requestUpdate();
  }

  goToToday(): void {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    this.pendingDate = today;
    this.startInputValue = toDisplay(today);
    this.displayYear = today.getFullYear();
    this.displayMonth = today.getMonth();
    dispatch<{ date: string | null }>(this._host, 'asc-select', {
      detail: { date: toDisplay(today) },
    });
    this._host.requestUpdate();
  }

  applyPreset(key: string): void {
    const range = presetRange(key);
    if (!range) return;
    this.pendingStartDate = range.start;
    this.pendingEndDate = range.end;
    this._clickCount = 2;
    this.startInputValue = toDisplay(range.start);
    this.endInputValue = toDisplay(range.end);
    // Navigate primary calendar to preset start month
    this.displayYear = range.start.getFullYear();
    this.displayMonth = range.start.getMonth();
    dispatch<{ startDate: string | null; endDate: string | null }>(this._host, 'asc-select', {
      detail: { startDate: toDisplay(range.start), endDate: toDisplay(range.end) },
    });
    this._host.requestUpdate();
  }

  parseInput(value: string, field: 'start' | 'end'): void {
    const parsed = parseDisplay(value);
    if (this._host.type === 'single') {
      // Single mode: only uses the start field; updates pendingDate and dispatches { date }
      if (parsed) {
        this._host._startInputError = false;
        this.pendingDate = parsed;
        this.startInputValue = value;
        this.displayYear = parsed.getFullYear();
        this.displayMonth = parsed.getMonth();
        dispatch<{ date: string | null }>(this._host, 'asc-select', {
          detail: { date: toDisplay(parsed) },
        });
      } else {
        this._host._startInputError = value.length > 0;
        this.startInputValue = value;
      }
    } else if (field === 'start') {
      if (parsed) {
        this._host._startInputError = false;
        this.pendingStartDate = parsed;
        this.startInputValue = value;
        this.displayYear = parsed.getFullYear();
        this.displayMonth = parsed.getMonth();
        dispatch<{ startDate: string | null; endDate: string | null }>(this._host, 'asc-select', {
          detail: {
            startDate: toDisplay(parsed),
            endDate: this.pendingEndDate ? toDisplay(this.pendingEndDate) : null,
          },
        });
      } else {
        this._host._startInputError = value.length > 0;
        this.startInputValue = value;
      }
    } else {
      if (parsed) {
        this._host._endInputError = false;
        this.pendingEndDate = parsed;
        this.endInputValue = value;
        dispatch<{ startDate: string | null; endDate: string | null }>(this._host, 'asc-select', {
          detail: {
            startDate: this.pendingStartDate ? toDisplay(this.pendingStartDate) : null,
            endDate: toDisplay(parsed),
          },
        });
      } else {
        this._host._endInputError = value.length > 0;
        this.endInputValue = value;
      }
    }
    this._host.requestUpdate();
  }

  // ── Confirm / cancel ─────────────────────────────────────────────────────

  confirmPending(): void {
    if (this._host.type === 'single') {
      this._host._confirmedDate = this.pendingDate ? toDisplay(this.pendingDate) : null;
    } else {
      this._host._confirmedStartDate = this.pendingStartDate ? toDisplay(this.pendingStartDate) : null;
      this._host._confirmedEndDate = this.pendingEndDate ? toDisplay(this.pendingEndDate) : null;
    }
    this._host.requestUpdate();
  }

  cancelPending(): void {
    // Revert pending state to confirmed
    if (this._host.type === 'single') {
      const confirmed = this._host._confirmedDate;
      this.pendingDate = confirmed ? parseDisplay(confirmed) : null;
      this.startInputValue = confirmed ?? '';
    } else {
      const cs = this._host._confirmedStartDate;
      const ce = this._host._confirmedEndDate;
      this.pendingStartDate = cs ? parseDisplay(cs) : null;
      this.pendingEndDate = ce ? parseDisplay(ce) : null;
      this.startInputValue = cs ?? '';
      this.endInputValue = ce ?? '';
      this._clickCount = cs ? 2 : 0;
    }
    this._host._startInputError = false;
    this._host._endInputError = false;
    this._host.requestUpdate();
  }

  // ── Calendar rendering ───────────────────────────────────────────────────

  renderCalendar(which: 'primary' | 'secondary'): TemplateResult {
    const year = which === 'primary'
      ? this.displayYear
      : new Date(this.displayYear, this.displayMonth + 1, 1).getFullYear();
    const month = which === 'primary'
      ? this.displayMonth
      : new Date(this.displayYear, this.displayMonth + 1, 1).getMonth();

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Build 6-week grid starting from the Monday before/on the 1st
    const firstOfMonth = new Date(year, month, 1);
    const startDay = startOfWeek(firstOfMonth);

    const cells: Date[] = [];
    const cursor = new Date(startDay);
    for (let i = 0; i < 42; i++) {
      cells.push(new Date(cursor));
      cursor.setDate(cursor.getDate() + 1);
    }

    const monthLabel = firstOfMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

    return html`
      <table
        part="calendar"
        role="grid"
        aria-label=${monthLabel}
      >
        <thead part="weekday-header">
          <tr>
            ${WEEKDAYS.map((d, i) => html`
              <th scope="col" abbr=${WEEKDAY_FULL[i]}>${d}</th>
            `)}
          </tr>
        </thead>
        <tbody>
          ${this._renderWeeks(cells, month, year, today)}
        </tbody>
      </table>
    `;
  }

  private _renderWeeks(
    cells: Date[],
    month: number,
    _year: number,
    today: Date,
  ): TemplateResult[] {
    const selectedKey = this.pendingDate ? toKey(this.pendingDate) : null;
    const todayKey = toKey(today);
    // Roving tabindex: selected day > today (if in month) > first in-month day
    const todayInMonth = cells.some(c => toKey(c) === todayKey && c.getMonth() === month);
    const firstInMonth = cells.find(c => c.getMonth() === month);
    const defaultFocusKey = selectedKey ?? (todayInMonth ? todayKey : (firstInMonth ? toKey(firstInMonth) : todayKey));

    const rows: TemplateResult[] = [];
    for (let r = 0; r < 6; r++) {
      const week = cells.slice(r * 7, r * 7 + 7);
      rows.push(html`<tr>${week.map(d => this._renderCell(d, month, today, defaultFocusKey))}</tr>`);
    }
    return rows;
  }

  private _renderCell(date: Date, month: number, today: Date, defaultFocusKey: string): TemplateResult {
    const key = toKey(date);
    const isOutside = date.getMonth() !== month;
    const isToday = toKey(date) === toKey(today);

    const isDual = this._host.type === 'dual';
    if (isDual) {
      return this._renderDualCell(date, key, isOutside, isToday);
    }

    // Single mode
    const isSelected = this.pendingDate ? toKey(this.pendingDate) === key : false;
    const isTabbable = !isOutside && key === defaultFocusKey;

    return html`
      <td
        part="day-cell"
        role="gridcell"
        aria-selected=${isSelected ? 'true' : 'false'}
        aria-current=${isToday ? 'date' : 'false'}
        aria-disabled=${isOutside ? 'true' : 'false'}
        class=${classMap({
          'day--outside': isOutside,
          'day--today': isToday,
          'day--selected': isSelected,
        })}
      >
        <button
          type="button"
          class="day-btn"
          data-date=${key}
          tabindex=${isTabbable ? '0' : '-1'}
          ?disabled=${isOutside}
          @click=${isOutside ? null : () => this.selectDay(date)}
          @keydown=${isOutside ? null : (e: KeyboardEvent) => this._handleCellKeydown(e, date)}
        >
          <span class="day__inner">
            <span class="day__number">${date.getDate()}</span>
            ${isToday ? html`<span class="day__dot" aria-hidden="true"></span>` : ''}
          </span>
        </button>
      </td>
    `;
  }

  private _renderDualCell(date: Date, key: string, isOutside: boolean, isToday: boolean): TemplateResult {
    const startKey = this.pendingStartDate ? toKey(this.pendingStartDate) : null;
    const endKey = this.pendingEndDate ? toKey(this.pendingEndDate) : null;
    const hoverKey = this._hoverDate ? toKey(this._hoverDate) : null;

    const isStart = startKey === key;
    const isEnd = endKey === key;
    const isSelected = isStart || isEnd;

    // Effective end: confirmed end, or hover target while first point is chosen.
    // Collapse to null when hover === start so no half-band is drawn on a single-day selection.
    const rawEffectiveEndKey = endKey ?? (this._clickCount === 1 ? hoverKey : null);
    const effectiveEndKey = rawEffectiveEndKey === startKey ? null : rawEffectiveEndKey;
    const isHoverEnd = this._clickCount === 1 && hoverKey === key && !!startKey && !isStart;

    let isInRange = false;
    if (startKey && effectiveEndKey && startKey !== effectiveEndKey) {
      const d = date.getTime();
      const s = this.pendingStartDate!.getTime();
      const e = effectiveEndKey === endKey
        ? this.pendingEndDate!.getTime()
        : this._hoverDate!.getTime();
      const [lo, hi] = s < e ? [s, e] : [e, s];
      isInRange = d > lo && d < hi;
    }

    return html`
      <td
        part="day-cell"
        role="gridcell"
        aria-selected=${isSelected ? 'true' : 'false'}
        aria-current=${isToday ? 'date' : 'false'}
        aria-disabled=${isOutside ? 'true' : 'false'}
        class=${classMap({
          'day--outside': isOutside,
          'day--today': isToday,
          'day--selected': isSelected || isHoverEnd,
          'day--range-start': isStart && !!effectiveEndKey,
          'day--range-end': isEnd || isHoverEnd,
          'day--in-range': isInRange,
        })}
      >
        <button
          type="button"
          class="day-btn"
          data-date=${key}
          tabindex=${(isSelected || isToday) && !isOutside ? '0' : '-1'}
          ?disabled=${isOutside}
          @click=${isOutside ? null : () => this.selectDay(date)}
          @mouseenter=${isOutside ? null : () => this.setHover(date)}
          @mouseleave=${isOutside ? null : () => this.setHover(null)}
          @keydown=${isOutside ? null : (e: KeyboardEvent) => this._handleCellKeydown(e, date)}
        >
          <span class="day__inner">
            <span class="day__number">${date.getDate()}</span>
            ${isToday ? html`<span class="day__dot" aria-hidden="true"></span>` : ''}
          </span>
        </button>
      </td>
    `;
  }

  private _handleCellKeydown(e: KeyboardEvent, date: Date): void {
    let target: Date | null = null;
    switch (e.key) {
      case 'ArrowLeft':  target = new Date(date); target.setDate(date.getDate() - 1); break;
      case 'ArrowRight': target = new Date(date); target.setDate(date.getDate() + 1); break;
      case 'ArrowUp':    target = new Date(date); target.setDate(date.getDate() - 7); break;
      case 'ArrowDown':  target = new Date(date); target.setDate(date.getDate() + 7); break;
      case 'Home':       target = startOfWeek(date); break;
      case 'End':        target = endOfWeek(date); break;
      case 'PageUp':
        this.navigateMonth(-1);
        return;
      case 'PageDown':
        this.navigateMonth(1);
        return;
      case 'Enter':
      case ' ':
        e.preventDefault();
        this.selectDay(date);
        return;
      default: return;
    }
    if (target) {
      e.preventDefault();
      // If navigating outside current month, update display
      if (target.getMonth() !== this.displayMonth || target.getFullYear() !== this.displayYear) {
        this.displayYear = target.getFullYear();
        this.displayMonth = target.getMonth();
        this._host.requestUpdate();
      }
      // Scope the focus query to the same table as the current button to avoid
      // matching duplicate data-date values in the secondary calendar (dual mode).
      const eventTarget = e.target as HTMLElement;
      const containingTable = eventTarget.closest('table');
      requestAnimationFrame(() => {
        const key = toKey(target!);
        const scopeRoot = containingTable ?? this._host.shadowRoot;
        const cell = scopeRoot?.querySelector<HTMLElement>(`[data-date="${key}"]`);
        cell?.focus();
      });
    }
  }
}
